import React, { useState } from 'react';
import { 
  Zap, 
  ArrowLeft, 
  File, 
  Video, 
  BookOpen, 
  FileText, 
  CheckCircle,
  AlertTriangle,
  Loader,
  ChevronDown,
  Plus
} from 'lucide-react';
import styled from 'styled-components';
import axios from 'axios';

// --- Types ---
interface Student {
  _id: string;
  firstName: string;
  lastName: string;
  grade: string;
  email: string;
}
interface Resource {
  id: number;
  title: string;
  description: string;
  estimatedTime: string;
  format: string;
}
interface GeneratedResources {
  title: string;
  type: string;
  resources: Resource[];
}
interface ResourceGeneratorProps {
  student: Student;
  subject: string;
  onBack: () => void;
  onGenerate?: (resources: GeneratedResources) => void;
}

const Container = styled.div`
  padding: 2rem;
  background: #fff;
  border-radius: 1rem;
  max-width: 600px;
  margin: 2rem auto;
  box-shadow: 0 2px 8px rgba(0,0,0,0.06);
`;
const Header = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 2rem;
`;
const BackBtn = styled.button`
  background: none;
  border: none;
  margin-right: 1rem;
  cursor: pointer;
`;
const Title = styled.h2`
  font-size: 1.3rem;
  font-weight: 700;
`;
const StudentCard = styled.div`
  background: #ede9fe;
  border: 1px solid #c4b5fd;
  border-radius: 1rem;
  padding: 1rem;
  display: flex;
  align-items: center;
  margin-bottom: 1.5rem;
`;
const StudentAvatar = styled.div`
  width: 2.5rem;
  height: 2.5rem;
  border-radius: 50%;
  background: #a78bfa;
  color: #6d28d9;
  font-weight: bold;
  font-size: 1.3rem;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 1rem;
`;
const Section = styled.div`
  margin-bottom: 1.5rem;
`;
const SectionTitle = styled.h3`
  font-weight: 600;
  color: #6d28d9;
  margin-bottom: 0.5rem;
`;
const OptionGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.5rem;
`;
const OptionBtn = styled.button<{ selected: boolean }>`
  padding: 0.75rem;
  border-radius: 0.75rem;
  border: 1.5px solid;
  border-color: ${({ selected }) => selected ? '#a78bfa' : '#e5e7eb'};
  background: ${({ selected }) => selected ? '#ede9fe' : '#fff'};
  color: ${({ selected }) => selected ? '#6d28d9' : '#374151'};
  display: flex;
  align-items: center;
  font-weight: 500;
  cursor: pointer;
  transition: 0.15s;
`;
const FocusAreaBtn = styled.div<{ selected: boolean }>`
  padding: 0.75rem;
  border-radius: 0.75rem;
  border: 1.5px solid;
  border-color: ${({ selected }) => selected ? '#a78bfa' : '#e5e7eb'};
  background: ${({ selected }) => selected ? '#ede9fe' : '#fff'};
  display: flex;
  align-items: center;
  cursor: pointer;
  margin-bottom: 0.5rem;
`;
const FocusAreaCheck = styled.div<{ selected: boolean }>`
  width: 1.25rem;
  height: 1.25rem;
  border-radius: 50%;
  border: 1.5px solid #a78bfa;
  background: ${({ selected }) => selected ? '#a78bfa' : '#fff'};
  display: flex;
  align-items: center;
  justify-content: center;
  margin-left: 0.75rem;
`;
const AdvancedBtn = styled.button`
  color: #6d28d9;
  background: none;
  border: none;
  display: flex;
  align-items: center;
  font-size: 1em;
  font-weight: 500;
  margin-bottom: 0.5rem;
  cursor: pointer;
`;
const AdvancedBox = styled.div`
  margin-top: 0.5rem;
  padding: 1rem;
  border: 1px solid #e5e7eb;
  border-radius: 0.75rem;
`;
const GenerateBtn = styled.button<{ disabled?: boolean }>`
  width: 100%;
  padding: 1rem;
  border-radius: 0.75rem;
  font-weight: 600;
  font-size: 1.1em;
  background: ${({ disabled }) => disabled ? '#e5e7eb' : '#a78bfa'};
  color: ${({ disabled }) => disabled ? '#6b7280' : '#fff'};
  border: none;
  margin-top: 1rem;
  cursor: ${({ disabled }) => disabled ? 'not-allowed' : 'pointer'};
  display: flex;
  align-items: center;
  justify-content: center;
`;
const SuccessBox = styled.div`
  background: #dcfce7;
  border: 1px solid #bbf7d0;
  border-radius: 0.75rem;
  padding: 1rem;
  margin-bottom: 1.5rem;
  display: flex;
  align-items: center;
`;
const ResourceCard = styled.div`
  border: 1px solid #e5e7eb;
  border-radius: 0.75rem;
  background: #fff;
  padding: 1rem;
  margin-bottom: 1rem;
  display: flex;
  align-items: flex-start;
`;
const ResourceIcon = styled.div`
  background: #ede9fe;
  padding: 0.5rem;
  border-radius: 50%;
  margin-right: 1rem;
  display: flex;
  align-items: center;
  justify-content: center;
`;
const ResourceInfo = styled.div`
  flex: 1;
`;
const ResourceTags = styled.div`
  display: flex;
  gap: 0.5rem;
  margin-top: 0.5rem;
`;
const Tag = styled.span<{ color?: string }>`
  font-size: 0.85em;
  background: ${({ color }) => color || '#f3f4f6'};
  color: #6d28d9;
  padding: 0.2em 0.7em;
  border-radius: 999px;
`;

const RESOURCE_TYPES = [
  'quiz',
  'worksheet',
  'lesson',
  'video',
  'summary',
  'experiment',
  'explanation',
  'activity',
  'assessment',
  'practice',
  'remediation',
];

const ResourceGenerator: React.FC<ResourceGeneratorProps> = ({ student, subject, onBack, onGenerate }) => {
  const [selectedType, setSelectedType] = useState<string>('');
  const [focusArea, setFocusArea] = useState<string>('');
  const [concept, setConcept] = useState<string>('');
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatedResources, setGeneratedResources] = useState<GeneratedResources | null>(null);

  const handleGenerateResources = async () => {
    setIsGenerating(true);
    try {
      const query = `Generate a ${selectedType} for Grade ${student.grade} ${subject}, concept: ${concept}, focus: ${focusArea}`;
      const response = await axios.post('/api/wolfram/generate', { query });
      const wolframResult = response.data.result;
      const generated: GeneratedResources = {
        title: `${student.firstName}'s Learning Resources`,
        type: selectedType,
        resources: [
          {
            id: 1,
            title: `Wolfram Alpha Generated ${selectedType.charAt(0).toUpperCase() + selectedType.slice(1)}`,
            description: wolframResult,
            estimatedTime: '15 mins',
            format: selectedType
          }
        ]
      };
      setGeneratedResources(generated);
      onGenerate?.(generated);
    } catch (err) {
      setGeneratedResources(null);
      // Optionally show an error message to the user
    }
    setIsGenerating(false);
  };

  return (
    <Container>
      <Header>
        <BackBtn onClick={onBack}>
          <ArrowLeft size={24} />
        </BackBtn>
        <Title>Generate Resources</Title>
      </Header>

      <StudentCard>
        <StudentAvatar>
          {student.firstName[0]}{student.lastName[0]}
        </StudentAvatar>
        <div>
          <div style={{ fontWeight: 600 }}>{student.firstName} {student.lastName}</div>
          <div style={{ fontSize: '0.875rem', color: '#6b7280' }}>Grade {student.grade}</div>
        </div>
      </StudentCard>

      {generatedResources ? (
        <>
          <SuccessBox>
            <CheckCircle size={20} style={{ marginRight: '0.75rem', color: '#059669' }} />
            Resources generated successfully!
          </SuccessBox>

          {generatedResources.resources.map(resource => (
            <ResourceCard key={resource.id}>
              <ResourceIcon>
                {resource.format === 'quiz' ? <FileText size={20} /> : <BookOpen size={20} />}
              </ResourceIcon>
              <ResourceInfo>
                <div style={{ fontWeight: 600, marginBottom: '0.25rem' }}>{resource.title}</div>
                <div style={{ fontSize: '0.875rem', color: '#6b7280', marginBottom: '0.5rem' }}>
                  {resource.description}
                </div>
                <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>
                  Estimated time: {resource.estimatedTime}
                </div>
              </ResourceInfo>
            </ResourceCard>
          ))}

          <GenerateBtn onClick={() => setGeneratedResources(null)}>
            Generate New Resources
          </GenerateBtn>
        </>
      ) : (
        <>
          <Section>
            <SectionTitle>Resource Type</SectionTitle>
            <OptionGrid>
              {RESOURCE_TYPES.map(type => (
                <OptionBtn
                  key={type}
                  selected={selectedType === type}
                  onClick={() => setSelectedType(type)}
                >
                  {type.charAt(0).toUpperCase() + type.slice(1)}
                </OptionBtn>
              ))}
            </OptionGrid>
          </Section>

          <Section>
            <SectionTitle>Focus Area</SectionTitle>
            <input
              type="text"
              value={focusArea}
              onChange={e => setFocusArea(e.target.value)}
              placeholder="e.g. understanding, remediation, practice, application, etc."
              style={{ width: '100%', padding: '0.75rem', borderRadius: '0.5rem', border: '1px solid #e5e7eb', marginBottom: '1rem' }}
            />
          </Section>

          <Section>
            <SectionTitle>Concept / Strand / Substrand</SectionTitle>
            <input
              type="text"
              value={concept}
              onChange={e => setConcept(e.target.value)}
              placeholder="e.g. The Heart, Human Body, Fractions, etc."
              style={{ width: '100%', padding: '0.75rem', borderRadius: '0.5rem', border: '1px solid #e5e7eb', marginBottom: '1rem' }}
            />
          </Section>

          <AdvancedBtn onClick={() => setShowAdvanced(!showAdvanced)}>
            <ChevronDown
              size={20}
              style={{
                marginRight: '0.5rem',
                transform: showAdvanced ? 'rotate(180deg)' : 'none',
                transition: 'transform 0.2s'
              }}
            />
            Advanced Options
          </AdvancedBtn>

          {showAdvanced && (
            <AdvancedBox>
              {/* Add advanced options here */}
            </AdvancedBox>
          )}

          <GenerateBtn
            disabled={!selectedType || !focusArea || !concept}
            onClick={handleGenerateResources}
          >
            {isGenerating ? (
              <>
                <Loader size={20} style={{ marginRight: '0.5rem' }} />
                Generating Resources...
              </>
            ) : (
              <>
                <Zap size={20} style={{ marginRight: '0.5rem' }} />
                Generate Resources
              </>
            )}
          </GenerateBtn>
        </>
      )}
    </Container>
  );
};

export default ResourceGenerator; 